import createClient, { type Middleware } from "openapi-fetch";
import type { paths } from "./schema.d.ts";
import { authStore } from "./auth-store.ts";

export type ApiPaths = paths;

export interface ApiClientOptions {
  /** Base URL of the API, e.g. "/api" behind Nginx or "http://localhost:5000" in development. */
  baseUrl: string;
  /** Called when a request fails with 401 even after a refresh attempt; the portal redirects to login. */
  onUnauthenticated?: () => void;
  /** Session renewal endpoint; "/auth/refresh" for the portal, "/auth/telegram/refresh" for the Mini App. */
  refreshPath?: string;
}

/**
 * Typed client over the generated OpenAPI schema. Adds the bearer token, and on a 401 tries one
 * refresh through the HttpOnly cookie before retrying the original request.
 */
export function createApiClient(options: ApiClientOptions) {
  const client = createClient<paths>({ baseUrl: options.baseUrl, credentials: "include" });
  let refreshing: Promise<boolean> | null = null;

  async function refresh(): Promise<boolean> {
    refreshing ??= (async () => {
      try {
        const refreshPath = options.refreshPath ?? "/auth/refresh";
        const response = await fetch(`${options.baseUrl}${refreshPath}`, { method: "POST", credentials: "include" });
        if (!response.ok) return false;
        const body = (await response.json()) as { accessToken: string };
        authStore.set(body.accessToken);
        return true;
      } catch {
        return false;
      } finally {
        refreshing = null;
      }
    })();
    return refreshing;
  }

  const auth: Middleware = {
    onRequest({ request }) {
      const token = authStore.token;
      if (token && !request.headers.has("Authorization")) {
        request.headers.set("Authorization", `Bearer ${token}`);
      }
      return request;
    },
    async onResponse({ request, response }) {
      const isAuthRoute = new URL(request.url).pathname.includes("/auth/");
      if (response.status !== 401 || isAuthRoute) return response;
      if (await refresh()) {
        const retry = new Request(request, { headers: new Headers(request.headers) });
        retry.headers.set("Authorization", `Bearer ${authStore.token ?? ""}`);
        return fetch(retry);
      }
      authStore.set(null);
      options.onUnauthenticated?.();
      return response;
    },
  };
  client.use(auth);
  return client;
}

export type ApiClient = ReturnType<typeof createApiClient>;
